/**
 * RouteSync — two-way sync between URL and store navigation state.
 *
 * The store is the source of truth for what's rendered. The URL is a
 * serialized view of the navigation tuple `(app, page, engagementId)`.
 * Two effects keep them aligned:
 *
 *   1. URL → store (browser back/forward, direct URL, refresh)
 *      Reads `location.pathname` via `useLocation`, maps to state via
 *      `stateFromUrl`, dispatches if the derived state differs from the
 *      current store state.
 *
 *   2. Store → URL (app-level navigation: clicking a dock button, etc.)
 *      Watches the navigation tuple, maps to a URL via `urlFromState`,
 *      calls `navigate(...)` if the URL differs from `location.pathname`.
 *
 * Each effect guards with an equality check so a single user action never
 * triggers a feedback loop. The component itself renders nothing.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigation, useEngagement } from '../../stores/useAppStore';
import { stateFromUrl, urlFromState } from '../../services/routes';
import { CONFIG } from '../../constants';
import { isUserInLimitedDeployment } from '../../services/dockService';

function isEmbeddedRemoteApp(app: string): boolean {
  return app === CONFIG.CLARA_APP || app === CONFIG.WORKSPACE || app === CONFIG.AGENTS;
}



export function RouteSync(): null {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigation, navigateToPage } = useNavigation();
  const { activeEngagementId, setEngagement } = useEngagement();

  // On first mount, URL is authoritative. After hydration the store
  // takes over and store→URL effect runs. Without this ref, both effects
  // race on mount: the URL→store effect dispatches the right action while
  // the store→URL effect simultaneously pushes the stale store state back
  // to the URL — net result is the URL wins but the store gets reverted.
  const hydratedRef = useRef(false);
  const lastSyncedUrlRef = useRef<string>('');

  // The /demo gallery is a preview surface that lives outside the
  // store-driven contract. Skip both effects for those paths so the
  // demo doesn't trigger a redirect to /focus.
  const isDemoPath = location.pathname.startsWith('/demo');
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;

  // URL → store: fires on mount AND when the URL changes (browser
  // back/forward, programmatic navigate). Skips if the URL we're seeing
  // is one we just pushed ourselves (matching `lastSyncedUrlRef`).
  useEffect(() => {
    if (isDemoPath) return;
    if (lastSyncedUrlRef.current === currentUrl) {
      // This URL change came from us — don't dispatch back into the store.
      hydratedRef.current = true;
      return;
    }

    // Workspace feature is gated to limited deployment users.
    // If a non-enabled user deep-links to a workspace URL, force redirect to chat.
    if (location.pathname.startsWith('/workspace') && !isUserInLimitedDeployment()) {
      navigateToPage(CONFIG.WORKSPACE, CONFIG.FIXED_TAB_CLARA_AI_CHAT);
      setEngagement(null);
      lastSyncedUrlRef.current = '/#/chat';
      hydratedRef.current = true;
      navigate('/#/chat', { replace: true });
      return;
    }

    const derived = stateFromUrl(currentUrl);

    const navChanged =
      navigation.currentApp !== derived.app ||
      navigation.currentPage !== derived.page;
    const engagementChanged = derived.engagementId !== activeEngagementId;

    if (navChanged) {
      navigateToPage(derived.app, derived.page);
    }
    if (engagementChanged) {
      setEngagement(derived.engagementId);
    }

    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrl, isDemoPath]);

  // Store → URL: skipped on the initial render because the URL is
  // authoritative there. After hydration, every store mutation that
  // changes the derived URL pushes to the router.
  useEffect(() => {
    if (isDemoPath) return;
    if (!hydratedRef.current) return;

    // Remote app switches are rendered by BodyContent from store state. Keep
    // the address bar stable after hydration; direct URLs still hydrate above.
    if (isEmbeddedRemoteApp(navigation.currentApp)) return;

    const target = urlFromState({
      app: navigation.currentApp,
      page: navigation.currentPage,
      engagementId: activeEngagementId,
    });
    if (target !== currentUrl) {
      // Record the URL we're about to push so the URL→store effect
      // doesn't bounce it back through the dispatcher.
      lastSyncedUrlRef.current = target;
      navigate(target, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation.currentApp, navigation.currentPage, activeEngagementId, currentUrl, isDemoPath]);

  return null;
}

export default RouteSync;
